import {
  DisplayRightsCompensationModel,
  type DisplayRightsProposalSummary,
} from '@fluenceur/types';
import { formatCents } from '@/pages/collabs/brief-templates/_shared/format-money';
import type { TFunction } from 'i18next';

/**
 * The agreed money, in one line.
 *
 * Two lengths for two densities: `short` on a card, where the badge already
 * carries the status and the version, and `long` in the detail, where the line
 * has room to read as a sentence. Both are built from the shared summary alone
 * — the amount, its currency and the run time all travel with it.
 */
export type DisplayRightsTermsLength = 'short' | 'long';

/**
 * Localized summary of the terms, or null when nothing was ever proposed —
 * callers render nothing rather than an empty row.
 */
export function displayRightsTermsSummary(
  summary: DisplayRightsProposalSummary | null,
  locale: string,
  t: TFunction,
  length: DisplayRightsTermsLength = 'short'
): string | null {
  if (!summary) {
    return null;
  }

  const model =
    summary.compensationModel === DisplayRightsCompensationModel.FLAT ? 'flat' : 'monthly';

  return t(`displayRights.terms.${length}.${model}`, {
    // The shared money formatter: it drops the decimals on a round amount and
    // keeps them otherwise. Rounding here printed "76 €" for terms of 75,50 €
    // while the creator's own page showed the cents — the brand and the creator
    // reading two different prices for the same contract.
    amount: formatCents(summary.amountCents, summary.currencyCode, locale),
    count: summary.durationMonths,
  });
}
